import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useDeployment } from "../deployment/DeploymentContext";

const FEATURES = [
  "Drag-and-drop Handlebars template designer with live preview",
  "Connect any external data source and map its fields to your report",
  "Schedule reports and auto-email the results, single file or zipped",
];

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.15" />
      <path d="M4.5 8.2 6.8 10.5 11.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const IS_ADMIN_HOST = typeof window !== "undefined" && window.location.hostname.startsWith("admin.");

export default function Login() {
  const { login } = useAuth();
  const { isOnPrem, status } = useDeployment();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname: string } } };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password, IS_ADMIN_HOST || isOnPrem ? undefined : tenantSlug || undefined);
      const dest = location.state?.from?.pathname ?? (IS_ADMIN_HOST ? "/admin/overview" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-marketing">
        <div className="login-marketing-inner">
          <div className="login-wordmark">ReportForge</div>
          <div className="login-tagline">
            Build. Design. Deliver reports your clients actually read.
          </div>
          <ul className="login-features">
            {FEATURES.map((f) => (
              <li key={f}>
                <CheckIcon />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="code-block">
            {"<h2>{{report.displayName}}</h2>\n<p>Hello {{params.customerName}}</p>"}
          </div>
        </div>
      </div>

      <div className="login-form-side">
        <form className="login-card" onSubmit={handleSubmit}>
          <h2>{IS_ADMIN_HOST ? "Platform admin" : "Welcome back"}</h2>
          <p className="help-text">
            {IS_ADMIN_HOST
              ? "Sign in to manage tenants, plans, and self-host licenses."
              : isOnPrem && status?.organisationName
                ? `Sign in to ${status.organisationName}.`
                : "Sign in to your tenant workspace."}
          </p>

          {!IS_ADMIN_HOST && !isOnPrem && (
            <div className="form-row">
              <label htmlFor="tenantSlug">Tenant (optional for platform admins)</label>
              <input
                id="tenantSlug"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                placeholder="acme"
              />
            </div>
          )}

          <div className="form-row">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="form-row">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && <div className="error-text">{error}</div>}

          <button type="submit" className="btn-primary" disabled={submitting} style={{ marginTop: 12, width: "100%" }}>
            {submitting ? "Signing in..." : "Sign in"}
          </button>

          {!IS_ADMIN_HOST && !isOnPrem && (
            <p className="help-text" style={{ marginTop: 12, textAlign: "center" }}>
              New here? <Link to="/signup">Start a free trial</Link>
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
